import { DraftSelectorModal } from '@/features/check-in/components';
import { useDrafts } from '@/features/check-in/hooks/use-drafts';
import { useUnreadCount } from '@/features/notifications/hooks/use-notifications';
import { useAuthStatus } from '@/hooks/use-auth-status';
import { useResponsiveBreakpoint } from '@/hooks/use-responsive-breakpoint';
import { router } from 'expo-router';
import React, { createContext, useCallback, useContext, useState } from 'react';

export type ResponsiveShellContextType = {
  isDesktop: boolean;
  isLargeDesktop: boolean;
  isAuthenticated: boolean;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  onCheckIn: () => void;
  unreadCount: number | undefined;
};

const ResponsiveShellContext = createContext<ResponsiveShellContextType | null>(null);

export function useResponsiveShell() {
  const context = useContext(ResponsiveShellContext);
  if (!context) {
    throw new Error('useResponsiveShell must be used within a ResponsiveShellProvider');
  }
  return context;
}

// Optional hook that doesn't throw - for components that may be outside the provider
export function useResponsiveShellOptional() {
  return useContext(ResponsiveShellContext);
}

interface ResponsiveShellProviderProps {
  children: React.ReactNode;
}

export function ResponsiveShellProvider({ children }: ResponsiveShellProviderProps) {
  const breakpoint = useResponsiveBreakpoint();
  const isDesktop = breakpoint === 'desktop' || breakpoint === 'large-desktop';
  const isLargeDesktop = breakpoint === 'large-desktop';
  const authStatus = useAuthStatus();
  const isAuthenticated = authStatus === 'verified';
  const { data: unreadCount } = useUnreadCount();
  const { drafts, deleteDraft, refreshDrafts } = useDrafts();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const onCheckIn = useCallback(async () => {
    const currentDrafts = await refreshDrafts();
    if (currentDrafts.length > 0) {
      setShowDraftModal(true);
    } else {
      router.push('/(root)/check-in/quick');
    }
  }, [refreshDrafts]);

  // Handle starting a new check-in from modal
  const handleStartNew = useCallback(() => {
    setShowDraftModal(false);
    router.push('/(root)/check-in/quick');
  }, []);

  // Handle selecting a draft from modal
  const handleSelectDraft = useCallback((draftId: string) => {
    setShowDraftModal(false);
    router.push({
      pathname: '/(root)/check-in/quick',
      params: { draftId },
    });
  }, []);

  // Handle deleting a draft from modal
  const handleDeleteDraft = useCallback(async (draftId: string) => {
    try {
      await deleteDraft(draftId);
    } catch (error) {
      console.error('Failed to delete draft:', error);
    }
  }, [deleteDraft]);

  const value: ResponsiveShellContextType = {
    isDesktop,
    isLargeDesktop,
    isAuthenticated,
    drawerOpen,
    openDrawer,
    closeDrawer,
    onCheckIn,
    unreadCount,
  };

  return (
    <ResponsiveShellContext.Provider value={value}>
      {children}
      {/* Draft selector modal is global and available to all authenticated screens */}
      {isAuthenticated && (
        <DraftSelectorModal
          visible={showDraftModal}
          onClose={() => setShowDraftModal(false)}
          drafts={drafts}
          onSelectDraft={handleSelectDraft}
          onStartNew={handleStartNew}
          onDeleteDraft={handleDeleteDraft}
        />
      )}
    </ResponsiveShellContext.Provider>
  );
}
